// VH-67: ISO week helpers shared by HealthReportPicker, PerWeekColumns, RangeTotalsCard.
// Implements ISO-8601 week numbering identical to Python's date.isocalendar().

/** Number of ISO weeks in a given year (52 or 53). */
export function isoWeeksInYear(year) {
  // The trick: Dec 28 is always in the last ISO week of its year.
  const d = new Date(Date.UTC(year, 11, 28))
  return getIsoWeek(d).week
}

/** Returns { week, year } for a given Date in ISO-8601 ordering. */
export function getIsoWeek(date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
  // Thursday of this week determines the ISO year.
  const dayNum = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
  return { week, year: d.getUTCFullYear() }
}

/** Returns the Monday (UTC) of a given ISO week. */
export function isoWeekToDate(year, week) {
  // Jan 4 is always in ISO week 1.
  const jan4 = new Date(Date.UTC(year, 0, 4))
  const jan4Day = jan4.getUTCDay() || 7
  const week1Monday = new Date(jan4)
  week1Monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1)
  const target = new Date(week1Monday)
  target.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7)
  return target
}

/** ISO week ordering helper: -1 if a < b, 0 if equal, 1 if a > b. */
export function compareIsoWeek(aYear, aWeek, bYear, bWeek) {
  if (aYear !== bYear) return aYear < bYear ? -1 : 1
  if (aWeek !== bWeek) return aWeek < bWeek ? -1 : 1
  return 0
}

/** Number of weeks between two ISO weeks, inclusive on both ends. */
export function isoWeeksBetween(fromYear, fromWeek, toYear, toWeek) {
  const fromMonday = isoWeekToDate(fromYear, fromWeek)
  const toMonday = isoWeekToDate(toYear, toWeek)
  const diffDays = Math.round((toMonday - fromMonday) / 86400000)
  return Math.floor(diffDays / 7) + 1
}

/** Builds the ordered list of ISO weeks in the given range, inclusive. */
export function listIsoWeeks(fromYear, fromWeek, toYear, toWeek) {
  const out = []
  let y = fromYear
  let w = fromWeek
  while (compareIsoWeek(y, w, toYear, toWeek) <= 0) {
    out.push({ year: y, week: w })
    const max = isoWeeksInYear(y)
    if (w >= max) {
      y += 1
      w = 1
    } else {
      w += 1
    }
  }
  return out
}

/** Returns the ISO week immediately before today (UTC). */
export function previousIsoWeek(reference = new Date()) {
  const ref = new Date(Date.UTC(
    reference.getUTCFullYear(),
    reference.getUTCMonth(),
    reference.getUTCDate() - 7,
  ))
  return getIsoWeek(ref)
}

/** Format an ISO week label like "Week 13, 2026" or "KW 13, 2026" for display. */
export function formatIsoWeekKey(year, week) {
  return `${year}-W${String(week).padStart(2, '0')}`
}

/** Parse a "YYYY-Www" key into { year, week }. Returns null on invalid input. */
export function parseIsoWeekKey(key) {
  const m = /^(\d{4})-W(\d{1,2})$/.exec(key || '')
  if (!m) return null
  return { year: Number(m[1]), week: Number(m[2]) }
}
